import { useState, useEffect } from 'react';
import { Drawer } from 'vaul';
import { useRoute } from '../../context/RouteContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { RouteBuilder } from '../RouteBuilder/RouteBuilder';
import { RouteInfo } from '../RouteInfo/RouteInfo';
import { RouteWeather } from '../RouteWeather/RouteWeather';
import { RoutePOI } from '../RoutePOI/RoutePOI';
import { RouteOptions } from '../RouteOptions/RouteOptions';
import { RouteActions } from '../RouteActions/RouteActions';
import { SavedRoutes } from '../SavedRoutes/SavedRoutes';
import { CommunityPanel } from '../Community/CommunityPanel';
import { LegalModal } from '../Legal/LegalModal';
import { AccentPicker } from './AccentPicker';
import { applyTheme, getTheme } from '../../utils/theme';
import './Sidebar.css';

// Mobile bottom-sheet snap points: peek (handle + tabs) → half → full screen.
const SHEET_SNAPS = ['96px', 0.55, 1];

export function Sidebar({ onOpenProfile, onOpenCategory }) {
  const [tab, setTab] = useState('plan');
  const [legalTab, setLegalTab] = useState(null); // 'terms' | 'privacy' | 'refund'
  const [theme, setTheme] = useState(getTheme());
  const isMobile = useIsMobile();
  const [snap, setSnap] = useState(SHEET_SNAPS[1]);
  const { state } = useRoute();

  // Loading a route: jump to the Plan tab (so the loaded route is in view) and
  // minimize the mobile sheet to its peek so the map is revealed.
  useEffect(() => {
    if (state.mapFocus > 0) {
      setTab('plan');
      setSnap(SHEET_SNAPS[0]);
    }
  }, [state.mapFocus]);

  const toggleTheme = () => {
    const next = theme === 'auris' ? 'default' : 'auris';
    applyTheme(next);
    setTheme(next);
  };

  const header = (
    <div className="sidebar__header">
      <div className="sidebar__brand">
        <img src="/motoroam_logo.png" alt="" className="sidebar__logo-mark" />
        <div className="sidebar__brand-text">
          <span className="sidebar__wordmark">Motoroam</span>
          <span className="sidebar__tagline">Free route planning</span>
        </div>
      </div>
    </div>
  );

  const tabs = (
    <div className="sidebar__tabs">
      <button
        className={`sidebar__tab${tab === 'plan' ? ' sidebar__tab--active' : ''}`}
        onClick={() => setTab('plan')}
      >
        Plan
      </button>
      <button
        className={`sidebar__tab${tab === 'community' ? ' sidebar__tab--active' : ''}`}
        onClick={() => setTab('community')}
      >
        Community
      </button>
    </div>
  );

  const scroll = (
    <div className="sidebar__scroll">
      {tab === 'plan' && (
        <>
          <section className="sidebar__section">
            <RouteBuilder />
          </section>
          <div className="sidebar__divider" />
          <section className="sidebar__section sidebar__section--stack">
            <RouteInfo />
            <RouteWeather />
            <RoutePOI />
          </section>
          <div className="sidebar__divider" />
          <section className="sidebar__section">
            <RouteOptions />
          </section>
          <div className="sidebar__divider" />
          <section className="sidebar__section sidebar__section--actions-sticky">
            <RouteActions />
          </section>
          <div className="sidebar__divider" />
          <section className="sidebar__section">
            <SavedRoutes />
          </section>
        </>
      )}

      {tab === 'community' && (
        <section className="sidebar__section">
          <CommunityPanel onOpenProfile={onOpenProfile} onOpenCategory={onOpenCategory} />
        </section>
      )}
    </div>
  );

  const footer = (
    <div className="sidebar__footer">
      <div className="sidebar__footer-row">
        <button className="sidebar__legal-link" onClick={() => setLegalTab('terms')}>Terms</button>
        <span className="sidebar__legal-sep">·</span>
        <button className="sidebar__legal-link" onClick={() => setLegalTab('privacy')}>Privacy</button>
        <span className="sidebar__legal-sep">·</span>
        <button className="sidebar__legal-link" onClick={() => setLegalTab('refund')}>Refunds</button>
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          title={theme === 'auris' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <span className="theme-toggle__dot" />
          {theme === 'auris' ? 'Dark' : 'Light'}
        </button>
        <AccentPicker />
      </div>
      <div className="sidebar__footer-tagline">Made with ❤️ in Sydney, Australia</div>
    </div>
  );

  const legal = legalTab && <LegalModal initialTab={legalTab} onClose={() => setLegalTab(null)} />;

  // Mobile: a draggable bottom sheet (peek → half → full). Non-modal so the map
  // stays interactive behind it; never dismissible (it's the primary UI).
  if (isMobile) {
    return (
      <>
        <Drawer.Root
          open
          modal={false}
          dismissible={false}
          snapPoints={SHEET_SNAPS}
          activeSnapPoint={snap}
          setActiveSnapPoint={setSnap}
        >
          <Drawer.Portal>
            <Drawer.Content className="sidebar-sheet" aria-describedby={undefined}>
              <Drawer.Title className="sr-only">Route panel</Drawer.Title>
              <div className="sidebar-sheet__handle" />
              {tabs}
              {scroll}
              {footer}
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
        {legal}
      </>
    );
  }

  return (
    <>
      <aside className="sidebar">
        {header}
        {tabs}
        {scroll}
        {footer}
      </aside>
      {legal}
    </>
  );
}
